// AI-CONTEXT: See docs/features/updates-log.md
// AI-CONTEXT: See docs/features/update-aanvragen.md
// AI-CONTEXT: See docs/patterns.md#data-static-release-bound-list

import { useEffect, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import ScrollToTop from '../../hooks/ScrollToTop'
import useSettings from '../../hooks/Settings'
import useUnseenUpdates from '../../hooks/useUnseenUpdates'
import SelectableGrid from '../../components/common/SelectableGrid'
import UpdateItem from '../../components/updates/UpdateItem'
import FeaturedUpdateCard from '../../components/updates/FeaturedUpdateCard'
import MyUpdateRequests from '../../components/updates/MyUpdateRequests'
import UpdateRequestForm from '../../components/updates/UpdateRequestForm'
import activeUpdateModules from '../../helpers/updates/activeUpdateModules'
import buildUpdateTabs, { ALLES } from '../../helpers/updates/buildUpdateTabs'
import filterUpdates from '../../helpers/updates/filterUpdates'
import featuredUpdates from '../../helpers/updates/featuredUpdates'
import groupUpdatesByMonth from '../../helpers/updates/groupUpdatesByMonth'
import resolveUpdatePageTab, {
  UPDATE_PAGE_TABS,
  UPDATE_PAGE_TAB_PARAM,
  TAB_NIEUW,
  TAB_AANVRAGEN,
  TAB_MIJN_UPDATES,
} from '../../helpers/updates/updatePageTabs'
import updates from '../../data/updates.json'

/**
 * De Updates-pagina. Drie panelen in een bovenliggende tabrij: wat er nieuw is
 * (statisch uit src/data/updates.json, dus mee met de release), het aanvragen
 * van een update, en de stand van zaken van de eigen aanvragen (allebei via
 * Firestore).
 *
 * De modulefilterrij hoort alleen bij het eerste paneel — dat is een ander
 * soort keuze dan de tabrij erboven en hoort niet in dezelfde rij.
 */
const Updates = () => {

  // State
  const [tab, setTab] = useState(ALLES)

  // Hooks
  ScrollToTop()
  const settings = useSettings()
  const history = useHistory()
  const { pathname, search } = useLocation()

  // Het geopende paneel komt uit de URL, niet uit state: dan werkt een directe
  // link (bijvoorbeeld vanuit een notificatie) en houdt de terugknop zich aan
  // wat de gebruiker deed.
  const paneel = resolveUpdatePageTab(new URLSearchParams(search).get(UPDATE_PAGE_TAB_PARAM))

  const openPaneel = (slug) => history.push(`${pathname}?${UPDATE_PAGE_TAB_PARAM}=${slug}`)

  // Zolang de instellingen laden is niet bekend welke modules deze organisatie
  // afneemt. Er wordt dan niets getoond — ook geen lege-lijst-melding, want die
  // slaat een tel later om en leest als een storing.
  // Zie docs/pitfalls.md#state-gate-defaults-open-while-loading
  const modules = settings.loaded ? activeUpdateModules(settings) : null

  const zichtbaar = modules ? filterUpdates(updates, modules) : null

  // Wie de pagina opent heeft de updates gezien: de badge in het menu gaat
  // uit. `unseen` komt van een listener, dus na de schrijfactie wordt hij 0
  // en draait dit effect niet nog een keer.
  const { unseen, markSeen } = useUnseenUpdates(settings)
  useEffect(() => {
    if (typeof unseen === 'number' && unseen > 0) markSeen()
  }, [unseen, markSeen])

  // Ook de tabbladen krijgen de modules mee: een update die bij twee
  // categorieën hoort komt in de lijst zodra één daarvan aanstaat, en zou
  // anders een tabblad opleveren voor een module die deze organisatie niet
  // afneemt.
  const tabs = buildUpdateTabs(zichtbaar || [], modules || [])

  // Afgeleid in plaats van opgeslagen: als de instellingen later binnenkomen kan
  // het gekozen tabblad verdwenen zijn, en dan zou een opgeslagen keuze een lege
  // lijst opleveren zonder dat de gebruiker ziet waarom.
  const actief = tabs.some(t => t.slug === tab) ? tab : ALLES

  // De uitgelichte verhalen staan als brede kaart bovenaan, maar alleen op
  // 'Alles'. Op een categorie-tabblad is de lijst al versmald, en dan verwart
  // een uitgelichte kaart erboven over waar je naar kijkt.
  const uitgelicht = zichtbaar ? featuredUpdates(zichtbaar, modules) : []
  const toonUitgelicht = actief === ALLES && uitgelicht.length > 0

  // Staat een verhaal als kaart op het scherm, dan hoort het niet óók nog als
  // regel in de maandenlijst: dezelfde wijziging twee keer leest als twee
  // wijzigingen. Op een categorie-tabblad is er geen kaart, en dan staat hij
  // juist wél in de lijst — anders is hij daar nergens te vinden.
  const alsKaart = new Set(toonUitgelicht ? uitgelicht.map(u => u.id) : [])

  const getoond = zichtbaar && (actief === ALLES
    ? zichtbaar.filter(update => !alsKaart.has(update.id))
    : zichtbaar.filter(update => update.modules.includes(actief)))

  const maanden = groupUpdatesByMonth(getoond || [])

  // JSX
  return (
    <div className="main">
      <div className="main-container">
        <div className="page-header">
          <h1>Updates</h1>
        </div>
        <div className="updates-page">

          <SelectableGrid
            items={UPDATE_PAGE_TABS}
            getId={(item) => item.slug}
            getLabel={(item) => item.label}
            selectedId={paneel}
            onClick={openPaneel}
          />

          {paneel === TAB_NIEUW && <>
            {tabs.length > 0 &&
              <div className="updates-tabs" role="tablist">
                {tabs.map(({ slug, label, count }) => (
                  <button
                    key={slug}
                    type="button"
                    role="tab"
                    aria-selected={slug === actief}
                    className={slug === actief ? 'updates-tab is-active' : 'updates-tab'}
                    onClick={() => setTab(slug)}
                  >
                    {label}
                    <span className="updates-tab-count">{count}</span>
                  </button>
                ))}
              </div>
            }

            {zichtbaar && zichtbaar.length === 0 &&
              <p className="updates-empty">Er zijn nog geen updates.</p>
            }

            {toonUitgelicht &&
              <div className="featured-updates">
                <h2 className="featured-updates-heading">Grote veranderingen</h2>
                {uitgelicht.map(update => (
                  <FeaturedUpdateCard
                    key={update.id}
                    update={update}
                    onOpen={(id) => history.push(`${pathname}/${id}`)}
                  />
                ))}
              </div>
            }

            {maanden.map(({ key, label, items }) => (
              <div className="updates-month" key={key}>
                <h2 className="updates-month-label">{label}</h2>
                {items.map(update => (
                  <UpdateItem
                    key={update.id}
                    update={update}
                    hideModule={actief === ALLES ? '' : actief}
                    visibleModules={tabs.filter(t => t.slug !== ALLES).map(t => t.slug)}
                  />
                ))}
              </div>
            ))}
          </>}

          {paneel === TAB_AANVRAGEN && <UpdateRequestForm />}

          {paneel === TAB_MIJN_UPDATES && <MyUpdateRequests />}

        </div>
      </div>
    </div>
  )
}

export default Updates
